import * as React from 'react';
import { Plus, Trash2, Volume2 } from 'lucide-react';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import type { ActivityFormData } from '../ActivityEditorPage.types';
import styles from './TypeSpecificFields.module.scss';

interface AudioTranslationSelectFieldsProps {
  audioText: string;
  audioUrl?: string | null;
  options: string[];
  correctAnswerIndex: number;
  onChange: (updates: Partial<ActivityFormData>) => void;
  /** Called when admin clicks "Generar audio". Returns the generated URL. Only provided in edit mode with a saved activity. */
  onGenerateAudio?: () => Promise<string>;
  errors?: {
    audioText?: string;
    options?: string;
  };
}

export function AudioTranslationSelectFields({
  audioText,
  audioUrl,
  options,
  correctAnswerIndex,
  onChange,
  onGenerateAudio,
  errors,
}: AudioTranslationSelectFieldsProps) {
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [generateError, setGenerateError] = React.useState<string | null>(null);

  const handleGenerateAudio = async () => {
    if (!onGenerateAudio) return;
    setIsGenerating(true);
    setGenerateError(null);
    try {
      const url = await onGenerateAudio();
      onChange({ audioUrl: url });
    } catch {
      setGenerateError('Error al generar el audio. Inténtalo de nuevo.');
    } finally {
      setIsGenerating(false);
    }
  };
  const handleOptionChange = (index: number, value: string) => {
    const newOptions = [...options];
    newOptions[index] = value;
    onChange({ options: newOptions });
  };

  const handleAddOption = () => {
    onChange({ options: [...options, ''] });
  };

  const handleRemoveOption = (index: number) => {
    if (options.length <= 2) return;
    const newOptions = options.filter((_, i) => i !== index);
    const newCorrect = index < correctAnswerIndex
      ? correctAnswerIndex - 1
      : correctAnswerIndex >= newOptions.length
        ? newOptions.length - 1
        : correctAnswerIndex;
    onChange({ options: newOptions, correctAnswerIndex: newCorrect });
  };

  return (
    <div className={styles.typeFields}>
      <h3 className={styles.typeFields__title}>Escuchar audio y seleccionar traducción</h3>
      <p className={styles.typeFields__description}>
        La IA genera este clip de audio a partir del texto. El alumno escucha y elige la traducción correcta al español.
      </p>

      <div>
        <label htmlFor="audioText" className={styles.typeFields__title}>
          Texto del audio (frase del script)
        </label>
        <Input
          id="audioText"
          value={audioText}
          onChange={(e) => onChange({ audioText: e.target.value })}
          placeholder="Ej: Good morning, everyone!"
        />
        <p className={styles.typeFields__hint}>
          Debe ser una palabra o frase corta del script de la historia.
        </p>
        {errors?.audioText && (
          <span className={styles.typeFields__error}>{errors.audioText}</span>
        )}
      </div>

      <div className={styles.typeFields__audioRow}>
        {audioUrl ? (
          <div className={styles.typeFields__audioStatus}>
            <span className={styles.typeFields__audioUrl} title={audioUrl}>
              Audio: {audioUrl.split('/').pop()}
            </span>
            {onGenerateAudio && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                leftIcon={<Volume2 />}
                onClick={handleGenerateAudio}
                disabled={isGenerating || !audioText.trim()}
              >
                {isGenerating ? 'Generando...' : 'Regenerar audio'}
              </Button>
            )}
          </div>
        ) : onGenerateAudio ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            leftIcon={<Volume2 />}
            onClick={handleGenerateAudio}
            disabled={isGenerating || !audioText.trim()}
          >
            {isGenerating ? 'Generando...' : 'Generar audio'}
          </Button>
        ) : (
          <p className={styles.typeFields__hint}>
            Guarda la actividad primero para poder generar el audio.
          </p>
        )}
        {generateError && (
          <span className={styles.typeFields__error}>{generateError}</span>
        )}
      </div>

      <div>
        <label className={styles.typeFields__title}>
          Opciones de traducción ({options.length}) — marca la correcta con el radio
        </label>
        <div className={styles.typeFields__list}>
          {options.map((option, index) => (
            <div key={index} className={styles.typeFields__optionItem}>
              <div className={styles.typeFields__optionRadio}>
                <input
                  type="radio"
                  className={styles.typeFields__radio}
                  name="correctAnswerIndex-ats"
                  checked={correctAnswerIndex === index}
                  onChange={() => onChange({ correctAnswerIndex: index })}
                  aria-label={`Marcar opción ${index + 1} como correcta`}
                />
              </div>
              <div className={styles.typeFields__optionInput}>
                <Input
                  value={option}
                  onChange={(e) => handleOptionChange(index, e.target.value)}
                  placeholder={`Traducción ${index + 1}`}
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                leftIcon={<Trash2 />}
                onClick={() => handleRemoveOption(index)}
                disabled={options.length <= 2}
                aria-label={`Eliminar opción ${index + 1}`}
              >
                <span className="sr-only">Eliminar</span>
              </Button>
            </div>
          ))}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          leftIcon={<Plus />}
          onClick={handleAddOption}
          className={styles.typeFields__addButton}
        >
          Añadir opción
        </Button>
        {errors?.options && (
          <span className={styles.typeFields__error}>{errors.options}</span>
        )}
      </div>
    </div>
  );
}
